import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { SyntheticEvent } from 'react';

import { Radius, Sizes, Spacing, Typography } from 'chatie-design-system';
import { Comment, DoubleBalloon, Icon, Tap } from 'chatie-design-system/icon';

import EFinderImage, {
  EFinderImageSizes,
} from '@components/common/EFinderImage';
import RoundAvatar from '@components/common/RoundAvatar';
import SvgIcon from '@components/common/SvgIcon';

import ProfileImg from '@public/icons/profile_32.svg';

import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { IPeekRoomInfo, IUserDetail } from '@model';
import { formatNumberMillion } from '@utils/chat';
import { useWebView } from '@utils/webview';

const cardContainerCSS = (theme: Theme) => css`
  display: flex;
  padding: ${Spacing.small}px;
  border-radius: ${Radius.small}px;

  .imageContainer {
    width: 50px;
    height: 50px;
    border-radius: ${Radius.small}px;
    margin-right: ${Spacing.small}px;
    position: relative;
    overflow: hidden;
    align-self: center;
  }
  .image {
    width: 50px;
    height: 50px;
    background-color: ${theme.var.gray200};
    border-radius: ${Radius.small}px;
    margin-right: ${Spacing.small}px;
  }
  .infoContainer {
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    overflow: hidden;
  }
  .name {
    ${Typography.h6};
  }
  .peek-ment {
    margin: ${Spacing.xxsmall}px 0px;
    ${Typography.body12};
    line-height: 145%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  .tags-container {
    display: flex;
    flex-direction: row;
    gap: 4px;
    flex-wrap: wrap;
    max-height: 26px;
    white-space: nowrap;
    overflow: hidden;

    & > p {
      color: ${theme.var.gray400};
      ${Typography.body11};
    }
  }
  .stat-container {
    display: flex;
    margin-top: 10px;
    gap: 10px;
  }
  .tab-container,
  .balloon-container {
    display: flex;
    flex-direction: row;
    gap: 2px;

    & p {
      ${Typography.body12};
    }

    .tab {
      color: ${theme.colors.main500};
    }
    .balloon {
      color: ${theme.var.gray500};
    }
  }
`;

const profileCSS = css`
  display: flex;
  justify-content: flex-start;
  height: 12px;
  margin-top: ${Spacing.small}px;

  p {
    ${Typography.body12};
    margin-left: ${Spacing.xxhsmall}px;
  }
`;

type PeekRoomProps = {
  peekInfo: IPeekRoomInfo;
  user: IUserDetail | undefined;
  onClickCard: (id: number) => void;
  onClickProfile: (e: SyntheticEvent, id: number) => void;
};

const PeekRoomCard = ({
  peekInfo,
  user,
  onClickCard,
  onClickProfile,
}: PeekRoomProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { web } = useWebView();
  const { platform, goodsName } = useGlobalContext();
  const router = useRouter();
  const isAnonimous = !!peekInfo?.peek?.is_anonymous;
  const peekRoomOwnerId = peekInfo?.user_id;

  const handleClickProfile = (e: SyntheticEvent) => {
    e.stopPropagation();

    const url = `/users/${peekRoomOwnerId}`;
    if (platform === Platform.web) {
      return router.push(url);
    } else {
      web.openPage(url);
    }
  };

  return (
    <div
      css={cardContainerCSS}
      onClick={() => onClickCard(peekInfo.id)}
      key={peekInfo.id}
    >
      <div
        className="imageContainer"
        onClick={(e) => onClickProfile(e, peekInfo?.character?.id as number)}
      >
        {peekInfo.character.profile_image &&
        peekInfo.character.profile_image.key ? (
          <EFinderImage
            image={peekInfo.character.profile_image}
            h={EFinderImageSizes.avatar}
            alt={'profile_img'}
          />
        ) : (
          <div className="image">
            <SvgIcon icon={ProfileImg} d={50} />
          </div>
        )}
      </div>
      <div className="infoContainer">
        <h5 className="name">{peekInfo.character.name}</h5>
        <p className="peek-ment">{peekInfo.peek?.hooking_ment}</p>
        <div className="tags-container">
          {peekInfo.character?.tags?.map((tag) => {
            return <p key={tag.id}>{t('#' + tag.name)}</p>;
          })}
        </div>
        <div className="stat-container">
          <div className="tab-container">
            <Icon icon={Tap} size={Sizes.xsmall} color={theme.colors.main500} />
            <p className="tab">
              {t(formatNumberMillion(peekInfo.peek.tap_count ?? 0))}
            </p>
          </div>
          {!!peekInfo.message_consumed_balloon && (
            <div className="balloon-container">
              <Icon
                icon={DoubleBalloon}
                size={Sizes.xsmall}
                color={theme.var.gray500}
              />
              <p className="balloon">
                {t(
                  formatNumberMillion(peekInfo?.message_consumed_balloon ?? 0) +
                    ` ${goodsName}`
                )}
              </p>
            </div>
          )}

          <div className="balloon-container">
            <Icon
              icon={Comment}
              size={Sizes.xsmall}
              color={theme.var.gray500}
            />

            <p className="balloon">
              {t(formatNumberMillion(peekInfo?.peek?.comment_count))}
            </p>
          </div>
        </div>
        {!isAnonimous && user && (
          <div css={profileCSS} onClick={handleClickProfile}>
            <RoundAvatar size={12} picture={user?.picture} />
            <p>{t(user?.nickname)}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PeekRoomCard;
